import type { Celda, CeldaEstado, PoseCelda, TestSet } from './tipos';

/**
 * Máquina de estados de las celdas de una corrida (spec 2026-08-23):
 *   pendiente → disparada → completa | fallida | sin_resultado
 * Completitud por CONTEO de filas por execution_id (regla del proyecto: nunca
 * "estabilidad corta"); sin fila tras el timeout → sin_resultado (el 4xx temprano
 * no deja fila). Todo puro: la persistencia vive en setsApi, el disparo en ejecutar.
 */

const FINALES: ReadonlySet<CeldaEstado> = new Set(['completa', 'fallida', 'sin_resultado']);
export const TIMEOUT_SIN_RESULTADO_MS = 10 * 60_000;

/**
 * Expansión por pose: una config con N poses seleccionadas produce N celdas por
 * producto, cada una con un config_id DERIVADO estable (`base::p:<poseId>` /
 * `base::c:<customId>`). La derivación es determinística, así dos corridas del
 * mismo set alinean celda a celda — y las evaluaciones (keyed por config_id)
 * distinguen poses sin cambiar el schema.
 */
function clavesDePose(config: TestSet['configs'][number]): string[] {
  const claves = [
    ...(config.payload.poses ?? []).map((id) => `p:${id}`),
    ...(config.payload.poses_custom ?? []).map((c) => `c:${c.id}`),
  ];
  return claves.length > 0 ? claves : [''];
}

export function configBaseId(configIdDerivado: string): string {
  const i = configIdDerivado.indexOf('::');
  return i === -1 ? configIdDerivado : configIdDerivado.slice(0, i);
}

/** Resuelve la pose que le toca a una celda expandida (null = default del backend). */
export function poseDeCelda(
  set: Pick<TestSet, 'configs'>,
  configIdDerivado: string,
): PoseCelda {
  const i = configIdDerivado.indexOf('::');
  if (i === -1) return null;
  const clave = configIdDerivado.slice(i + 2);
  const base = set.configs.find((c) => c.config_id === configBaseId(configIdDerivado));
  if (clave.startsWith('p:')) return { tipo: 'catalogo', id: clave.slice(2) };
  if (clave.startsWith('c:')) {
    const id = clave.slice(2);
    const custom = base?.payload.poses_custom?.find((c) => c.id === id);
    return custom ? { tipo: 'custom', id, filename: custom.filename } : null;
  }
  return null;
}

export function crearCeldas(set: Pick<TestSet, 'variante_ids' | 'configs'>): Celda[] {
  return set.configs.flatMap((config) => {
    // Productos propios de la config si los tiene; sino hereda los del set.
    const variantes = config.variante_ids?.length ? config.variante_ids : set.variante_ids;
    return clavesDePose(config).flatMap((clave) =>
      variantes.map((varianteId) => ({
        config_id: clave ? `${config.config_id}::${clave}` : config.config_id,
        variante_id: varianteId,
        execution_id: crypto.randomUUID(),
        estado: 'pendiente' as const,
        disparada_en: null,
      })),
    );
  });
}

export function aplicarPoll(
  celdas: Celda[],
  filas: Array<{ execution_id: string; has_error: boolean | null }>,
  ahoraMs: number,
  timeoutMs: number = TIMEOUT_SIN_RESULTADO_MS,
): Celda[] {
  const porExecution = new Map(filas.map((f) => [f.execution_id, f]));
  return celdas.map((c) => {
    if (FINALES.has(c.estado) || c.estado === 'pendiente') return c;
    const fila = porExecution.get(c.execution_id);
    if (fila) return { ...c, estado: fila.has_error ? 'fallida' : 'completa' };
    if (c.disparada_en != null && ahoraMs - c.disparada_en > timeoutMs) {
      return { ...c, estado: 'sin_resultado' };
    }
    return c;
  });
}

export function celdasPendientes(celdas: Celda[]): Celda[] {
  return celdas.filter((c) => c.estado === 'pendiente');
}

export function corridaTerminada(celdas: Celda[]): boolean {
  return celdas.length > 0 && celdas.every((c) => FINALES.has(c.estado));
}

/** Reintento manual: execution_id NUEVO (el conteo nunca mezcla intentos). */
export function reintentarCelda(celdas: Celda[], configId: string, varianteId: string): Celda[] {
  return celdas.map((c) =>
    c.config_id === configId && c.variante_id === varianteId
      ? { ...c, execution_id: crypto.randomUUID(), estado: 'pendiente', disparada_en: null }
      : c,
  );
}
